<template lang="pug">
  .product
    .product__row#productRow
      .product__col.product__col--gallery
        Gallery(:product="singleProduct")
      .product__col.product__col--info
        affix.product__info(relative-element-selector="#productRow" :offset="{ top: 100, bottom: 0 }"
                            :enabled="$mq !== 'mobile' || $mq !== 'tablet'")
          Breadcrumbs(:breadcrumbsList="breadcrumbsList")
          Info(:product="singleProduct")

    Features(:product="singleProduct")

    section.latest-products
      .latest-products__head
        h3.title {{ $t('products.latest.title') }}
      ProductsList(:products="latestProductsFiltered")

    //- StaticContent(:content="staticContent")
</template>

<script>
import { mapState } from 'vuex'
import api from '@/api/index'
import Breadcrumbs from '@/components/partials/Breadcrumbs'
import Gallery from '@/components/single-product/Gallery'
import Info from '@/components/single-product/Info'
import Features from '@/components/single-product/Features'
import ProductsList from '@/components/products/ProductsList'
import StaticContent from '@/components/partials/StaticContent'

export default {
  components: {
    Breadcrumbs,
    Gallery,
    Info,
    Features,
    ProductsList,
    StaticContent
  },

  data() {
    return {
      singleProduct: {},
      latestProducts: [],

      breadcrumbsList: [
        {
          link: this.localePath('index'),
          name: this.$t('breadcrumbs.home')
				},
				{
          link: this.localePath('catalog'),
          name: this.$t('breadcrumbs.allShoes')
        },
        {
          link: '',
          name: ''
        },
        {
          link: this.$route.path,
          name: ''
          // name: this.$route.params.slug.replace(/-/g, ' ')
        }
      ]
    }
  },

  head() {
		let productMarkup = {
			"@context": "https://schema.org/",
			"@type": "Product",
			"name": this.singleProduct.name,
			"image": this.singleProduct.images[0].src,
			"description": this.singleProduct.description,
			"brand": 'SITELLE',
			"offers": {
				"@type": "Offer",
				"url": this.singleProduct.permalink,
				"priceCurrency": this.$store.state.geoData.currency,
				"price": this.singleProduct.price,
				"priceValidUntil": `${new Date().getFullYear()}-${new Date().getMonth()}-${new Date().getDate()}`,
				"itemCondition": "https://schema.org/UsedCondition",
				"availability": "https://schema.org/InStock",
				"seller": {
					"@type": "Organization",
					"name": "SITELLE"
				}
			}
		},
		breadcrumbsMarkup = {
			"@context": "https://schema.org",
			"@type": "BreadcrumbList",
			"itemListElement": [{
				"@type": "ListItem",
				"position": 1,
				"name": this.$t('breadcrumbs.home'),
				"item": 'https://sitelle.ua' + this.localePath('index')
			},{
				"@type": "ListItem",
				"position": 2,
				"name": this.$t('breadcrumbs.allShoes'),
				"item": 'https://sitelle.ua' + this.localePath('catalog')
			},{
				"@type": "ListItem",
				"position": 3,
				"name": this.categoryName,
				"item": 'https://sitelle.ua' + this.localePath({
																								name: 'product-category-slug',
																								params: { slug: this.categorySlug.split('-')[0] }
																							})
			},{
				"@type": "ListItem",
				"position": 4,
				"name": this.singleProduct.name,
				"item": 'https://sitelle.ua' + this.$route.path
			}]
		};

    return {
			title: this.$t('meta.product.title1') + this.singleProduct.name + ' - ' + this.singleProduct.categories[0].name + ' SITELLE',
      titleTemplate: null,
      meta: [
        {
          hid: 'description',
					name: 'description',
          content: this.$t('meta.product.description1') + this.singleProduct.name + this.$t('meta.product.description2') + this.singleProduct.name + this.$t('meta.product.description3')
        },
        {
          hid: 'og:title',
					property: 'og:title',
          content: this.$t('meta.product.title1') + this.singleProduct.name + ' - ' + this.singleProduct.categories[0].name + ' SITELLE'
        },
        {
          hid: 'og:description',
					property: 'og:description',
					content: this.$t('meta.product.description1') + this.singleProduct.name + this.$t('meta.product.description2') + this.singleProduct.name + this.$t('meta.product.description3')
				},
        {
          hid: 'og:image',
          property: 'og:image',
          content: this.singleProduct.acf.feature_image
        }
			],	
			script: [
				{
					type: "application/ld+json",
					innerHTML: JSON.stringify(productMarkup)
				},
				{
					type: "application/ld+json",
					innerHTML: JSON.stringify(breadcrumbsMarkup)
				}
			]
    }
  },

  computed: {
    ...mapState({
      staticContent: state => state.content.data
    }),

    categorySlug() {
      return this.singleProduct.categories[0].slug
    },

    categoryName() {
      return this.singleProduct.categories[0].name
    },

    latestProductsFiltered() {
      return this.latestProducts
        .filter(item => item.id !== this.singleProduct.id)
        .slice(0, 3)
    }
  },

  async asyncData({ store, params }) {
    const product = await api.getSingleProduct(
      params.slug,
      store.state.geoData.locale,
      store.state.geoData.currency
    )
    const latestProducts = await api.getLatestProducts(
      store.state.geoData.locale,
      store.state.geoData.currency
    )

    return {
      singleProduct: product[0],
      latestProducts: latestProducts
    }
	},

  created() {
    this.breadcrumbsList[2].link = this.localePath({
      name: 'product-category-slug',
      params: { slug: this.categorySlug.split('-')[0] }
    })
    this.breadcrumbsList[2].name = this.categoryName
    this.breadcrumbsList[3].name = this.singleProduct.name
  }
}
</script>

<style lang="scss" scoped>
@import '~assets/scss/helpers/vars';
@import '~assets/scss/helpers/mixins';

.product {
  padding-top: 20px;
	padding-bottom: 100px;

	@include mobile {
		padding-bottom: 50px;
	}

  &__row {
    display: flex;
    flex-wrap: wrap;
    margin-left: -15px;
    margin-right: -15px;

    @include laptop {
      justify-content: space-between;
    }
  }

  &__col {
    padding-left: 15px;
    padding-right: 15px;

    &--gallery {
      flex: 0 0 100%;
      max-width: 655px;

      @include laptop {
        max-width: 55%;
      }

      @include tablet {
        display: none;
      }
    }

    &--info {
      flex: 0 0 100%;
      max-width: 375px;

      @include laptop {
        padding-top: 60px;
      }

      @include tablet {
        max-width: 100%;
        padding-top: 0;
      }
    }
  }

  &__info {
    width: 345px;

    @include tablet {
      position: static;
      width: 100%;
    }
  }
}

.latest-products {
  margin-top: 165px;

  @include laptop {
    margin-top: 100px;
  }

  @include tablet {
    margin-top: 80px;
  }

  @include mobile {
    margin-top: 60px;
  }

  &__head {
    margin-bottom: 30px;

    @include laptop {
      margin-bottom: 20px;
    }

    @include mobile {
      margin-bottom: 10px;
    }

    .title {
      margin-bottom: 10px;
      font-size: 24px;
      font-weight: 600;
      letter-spacing: 2px;
      line-height: 1.4;
      text-transform: uppercase;

      @include mobile {
        font-size: 20px;
      }
    }
  }
}
</style>
